/**
 * Pure optimistic GitHub PR mutation helpers (low CC for CRAP gate).
 */

import type { AttentionItem, CurrentUserIdentity } from '../../domain/types';
import {
  buildCurrentUserReviewer,
  isCurrentUserReviewer,
  matchesGitHubItem,
} from './useGitHubSlice';

export function applyOptimisticApproval(
  items: AttentionItem[],
  itemId: string,
  currentUser: CurrentUserIdentity | undefined,
): AttentionItem[] {
  const currentUserApproval = buildCurrentUserReviewer(currentUser);
  return items.map((item) => {
    if (!matchesGitHubItem(item, itemId)) return item;
    const existingReviewers = item.reviewers || [];
    const hasCurrentUserReviewer = existingReviewers.some((r) =>
      isCurrentUserReviewer(r, currentUser),
    );
    const updatedReviewers = hasCurrentUserReviewer
      ? existingReviewers.map((r) =>
          isCurrentUserReviewer(r, currentUser)
            ? { ...r, state: 'Aprobado' as const, isCurrentUser: true }
            : r,
        )
      : [...existingReviewers, { ...currentUserApproval, reviewedAt: new Date().toISOString() }];
    const allChecksOk = (item.checks || []).every((c) => c.conclusion === 'ok');
    return {
      ...item,
      reviewers: updatedReviewers,
      st: allChecksOk ? 'ready' : item.st,
      mergeTitle: allChecksOk ? 'Listo para mergear' : 'Aprobado · esperando CI',
      mergeText: allChecksOk ? 'Aprobado y todos los checks pasaron.' : 'Revisión aprobada.',
      action: 'Listo para merge',
    };
  });
}

export function applyOptimisticCommentToggle(
  items: AttentionItem[],
  itemId: string,
  index: number,
): AttentionItem[] {
  return items.map((item) => {
    if (!matchesGitHubItem(item, itemId) || !item.comments?.[index]) return item;
    const updatedComments = [...item.comments];
    const comment = updatedComments[index];
    if (comment.state === 'Aprobado') return item;
    const nextState = comment.state === 'Resuelto' ? 'Sin resolver' : 'Resuelto';
    updatedComments[index] = { ...comment, state: nextState, resolved: nextState === 'Resuelto' };
    return { ...item, comments: updatedComments };
  });
}
